"use client";

import { motion } from "framer-motion";
import { useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ShoppingBag, Sparkles, Plus } from "lucide-react";

type Product = {
  name: string;
  latin: string;
  image: string;
  tagline: string;
  description: string;
  badge?: string;
  benefits: string[];
  price: string;
};

const racines: Product[] = [
  {
    name: "Patate Douce",
    latin: "Ipomoea batatas",
    image: "/images/patate-douce.png",
    tagline: "La reine dorée",
    description:
      "Chair sucrée, peau violet-ambré. Riche en bêta-carotène, en fibres et en énergie lente. Idéale rôtie, en purée ou en frites.",
    badge: "Best-seller",
    benefits: ["Riche en vitamine A", "Index glycémique bas", "Fibres digestives"],
    price: "1 200 FCFA / kg",
  },
  {
    name: "Pomme de Terre",
    latin: "Solanum tuberosum",
    image: "/images/pomme-de-terre.png",
    tagline: "L'universelle",
    description:
      "Chair ferme, peau fine dorée. Récoltée à maturité optimale, elle se tient à la cuisson et révèle un goût de beurre noisette.",
    benefits: ["Source de potassium", "Vitamine C", "Sans gluten"],
    price: "900 FCFA / kg",
  },
  {
    name: "Manioc",
    latin: "Manihot esculenta",
    image: "/images/manioc.png",
    tagline: "Le pilier généreux",
    description:
      "Racine nourricière par excellence, base de l'attiéké, du placali et de la farine. Chair blanche, dense, énergétique.",
    badge: "Local star",
    benefits: ["Sans gluten", "Haute énergie", "Riche en amidon"],
    price: "600 FCFA / kg",
  },
  {
    name: "Igname",
    latin: "Dioscorea spp.",
    image: "/images/igname.png",
    tagline: "Le royal",
    description:
      "Tubercule noble des terres tropicales. Chair crème, texture satinée. Pilé en foutou, rôti ou en potage, il incarne le festin.",
    badge: "Édition noble",
    benefits: ["Fibres", "Potassium", "Antioxydants"],
    price: "1 000 FCFA / kg",
  },
];

const transformes: Product[] = [
  {
    name: "Farine de Manioc",
    latin: "Farine premium",
    image: "/images/farine-manioc.png",
    tagline: "L'or blanc",
    description:
      "Farine fine sans gluten, obtenue par fermentation naturelle et séchage solaire. Parfaite pour pains, crêpes et pâtisseries.",
    badge: "Sans gluten",
    benefits: ["100% naturel", "Sans additifs", "Longue conservation"],
    price: "2 500 FCFA / kg",
  },
  {
    name: "Chips de Patate",
    latin: "Artisanales",
    image: "/images/chips-patate.png",
    tagline: "Croustillant doré",
    description:
      "Tranches fines de patate douce, frites à l'huile de palme, légèrement salées. Le snacking noble, sans artifice.",
    badge: "Artisanal",
    benefits: ["Sans conservateurs", "Cuisson artisanale", "Goût naturel"],
    price: "1 500 FCFA / sachet 250g",
  },
  {
    name: "Attiéké",
    latin: "Couscous de manioc",
    image: "/images/attieke.png",
    tagline: "La tradition vivante",
    description:
      "Semoule de manioc fermentée, prête à déguster. L'accompagnement star des poissons grillés et des légumes.",
    badge: "Recette héritée",
    benefits: ["Fermentation naturelle", "Léger et digeste", "Prêt à l'emploi"],
    price: "1 800 FCFA / kg",
  },
];

function ProductCard({ product, index }: { product: Product; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay: index * 0.08 }}
    >
      <Card className="group relative overflow-hidden border-border hover:border-gold hover:shadow-gold-lg transition-all duration-500 rounded-2xl bg-card">
        {/* Image */}
        <div className="relative h-64 overflow-hidden">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-earth-deep/80 via-transparent to-transparent" />

          {product.badge && (
            <div className="absolute top-4 left-4">
              <Badge className="bg-gradient-gold text-white shadow-gold border-0 hover:bg-gradient-gold">
                <Sparkles className="w-3 h-3 mr-1" />
                {product.badge}
              </Badge>
            </div>
          )}

          <div className="absolute bottom-4 left-4 right-4">
            <div className="text-[10px] uppercase tracking-[0.28em] text-cream/70 font-semibold italic">
              {product.tagline}
            </div>
            <h3 className="font-display text-2xl font-bold text-cream">
              {product.name}
            </h3>
            <p className="text-cream/60 text-xs italic">{product.latin}</p>
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          <p className="text-sm text-muted-foreground leading-relaxed min-h-[80px]">
            {product.description}
          </p>

          <ul className="mt-4 space-y-1.5">
            {product.benefits.map((b, i) => (
              <li key={i} className="flex items-center gap-2 text-xs text-foreground/75">
                <span className="w-1.5 h-1.5 rounded-full bg-gradient-gold" />
                {b}
              </li>
            ))}
          </ul>

          <div className="mt-5 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Vente directe
              </div>
              <div className="font-display text-lg font-bold text-gradient-gold">
                {product.price}
              </div>
            </div>
            <Button
              size="sm"
              className="bg-gradient-gold text-white shadow-gold hover:shadow-gold-lg rounded-full"
              onClick={() =>
                document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })
              }
            >
              <Plus className="w-4 h-4 mr-1" />
              Commander
            </Button>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}

export function Products() {
  const [tab, setTab] = useState("racines");

  return (
    <section id="produits" className="relative py-24 lg:py-32 bg-gradient-to-b from-background to-secondary/40">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="divider-gold max-w-xs mx-auto mb-5"
          >
            <span className="text-xs uppercase tracking-[0.32em] text-gold-deep font-semibold">
              Notre Catalogue
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight"
          >
            Des <span className="text-gradient-gold">racines</span> et des{" "}
            <span className="text-gradient-gold">transformés</span> d&apos;exception
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed"
          >
            Quatre racines nobles, trois transformations d&apos;excellence. Chaque produit
            est sélectionné, lavé, trié et conditionné dans nos ateliers. Du producteur à
            votre table, sans intermédiaire.
          </motion.p>
        </div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-12"
        >
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <div className="flex justify-center">
              <TabsList className="grid w-full max-w-md grid-cols-2 bg-secondary/60 rounded-full p-1">
                <TabsTrigger
                  value="racines"
                  className="rounded-full data-[state=active]:bg-gradient-gold data-[state=active]:text-white data-[state=active]:shadow-gold transition-all"
                >
                  Racines &amp; Tubercules
                </TabsTrigger>
                <TabsTrigger
                  value="transformes"
                  className="rounded-full data-[state=active]:bg-gradient-gold data-[state=active]:text-white data-[state=active]:shadow-gold transition-all"
                >
                  Produits Transformés
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Racines grid */}
            <TabsContent value="racines" className="mt-12">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {racines.map((p, i) => (
                  <ProductCard key={p.name} product={p} index={i} />
                ))}
              </div>
            </TabsContent>

            {/* Transformés grid */}
            <TabsContent value="transformes" className="mt-12">
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {transformes.map((p, i) => (
                  <ProductCard key={p.name} product={p} index={i} />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </motion.div>

        {/* CTA strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-16 flex flex-col sm:flex-row items-center justify-center gap-4 p-6 rounded-2xl glass-card"
        >
          <ShoppingBag className="w-8 h-8 text-gold" />
          <p className="text-center sm:text-left text-foreground/85 font-medium">
            Vous souhaitez commander en gros ou passer un contrat d&apos;approvisionnement&nbsp;?
          </p>
          <Button
            onClick={() =>
              document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })
            }
            className="bg-gradient-gold text-white shadow-gold hover:shadow-gold-lg rounded-full whitespace-nowrap"
          >
            Demander un devis
          </Button>
        </motion.div>
      </div>
    </section>
  );
}
